import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrash2 } = FiIcons;

const Message = ({ message, currentUserId, onDelete }) => {
  const { t } = useLanguage();
  const isOwnMessage = message.user_id === currentUserId;

  const formatTime = (timestamp) => {
    return format(new Date(timestamp), 'HH:mm');
  };

  // 檢查字串是否只包含表情符號和空白
  const isOnlyEmojis = (text) => {
    if (!text) return false;

    // 移除所有空白字符
    const cleanText = text.replace(/\s/g, '');

    // 如果字符串為空，則不是表情符號
    if (cleanText.length === 0) return false;

    // 使用更準確的正則表達式來檢測表情符號
    // 這個正則考慮了複合表情符號（如國旗、職業等）
    const emojiRegex = /(?:\p{Emoji}[\u200D\u2640-\u2642\u2695-\u2696\u2708-\u270D\u2764\uFE0F]?(?:\uFE0F|\u200D(?:\p{Emoji}[\uFE0F]?))*|\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu;
    
    // 提取所有表情符號
    const emojis = [...cleanText.matchAll(emojiRegex)].map(m => m[0]);
    
    // 檢查提取的表情符號是否與原始字串（去除空格後）相同
    const emojiText = emojis.join('');
    
    // 檢查是否為純表情符號訊息，並且數量不超過5個
    return emojiText === cleanText && emojis.length <= 5;
  };

  // 決定表情符號的大小
  const getEmojiSize = (content) => {
    if (!content) return '';

    // 檢查是否為純表情符號訊息
    if (isOnlyEmojis(content)) {
      // 計算表情符號數量 - 使用更準確的方法
      const emojiRegex = /(?:\p{Emoji}[\u200D\u2640-\u2642\u2695-\u2696\u2708-\u270D\u2764\uFE0F]?(?:\uFE0F|\u200D(?:\p{Emoji}[\uFE0F]?))*|\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu;
      const emojis = [...content.replace(/\s/g, '').matchAll(emojiRegex)].map(m => m[0]);
      const emojiCount = emojis.length;
      
      // 根據表情符號數量決定大小
      if (emojiCount <= 3) {
        return 'text-4xl leading-relaxed'; // 1-3個表情符號使用最大尺寸
      } else if (emojiCount <= 5) {
        return 'text-3xl leading-relaxed'; // 4-5個表情符號使用中等尺寸
      }
    }
    
    // 混合內容或較長內容使用正常尺寸
    return '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}
    >
      <div className={`max-w-xs sm:max-w-md lg:max-w-lg ${isOwnMessage ? 'order-2' : 'order-1'}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-gray-600">
            {message.nickname}
          </span>
          <span className="text-xs text-gray-400">
            {formatTime(message.created_at)}
          </span>
          {isOwnMessage && !message.deleted && (
            <button
              onClick={() => onDelete(message.id)}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title={t('delete')}
            >
              <SafeIcon icon={FiTrash2} className="w-3 h-3" />
            </button>
          )}
        </div>
        <div
          className={`rounded-2xl px-4 py-2 ${
            isOwnMessage ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {message.deleted ? (
            <span className="italic text-gray-500">
              {t('messageDeleted')}
            </span>
          ) : (
            <p className={`whitespace-pre-wrap break-words ${getEmojiSize(message.content)}`}>
              {message.content}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Message;